/**
 * Volunteer edit form.
 * Loads the volunteer, hydrates the form, then PATCHes profile / status / engagement in one submit.
 */
import * as React from 'react';
import { User, BookOpen, Clock, Building2, Calendar, Mail, UserCog } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { SmartContactInput } from '@/components/forms/SmartContactInput';
import { Select } from '@/components/forms/Select';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { AvailabilityGrid } from '@/components/forms/AvailabilityGrid';
import { DateSplitInput } from '@/components/forms/DateSplitInput';
import { getVolunteer, updateVolunteer } from '@/services/volunteers.service';
import { fetchRoles } from '@/services/roles.service';
import { useEstablishmentsMultiselect } from '@/hooks/useEstablishmentsMultiselect';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { useEnrichEstablishmentLabels } from '@/hooks/useEnrichEstablishmentLabels';
import { getSubjects } from '@/services/subjects.service';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { buildCreatableRoleOptions, getEditableRoles } from '@/utils/interface-hierarchy-utils';
import type { EditVolunteerFormState, UpdateVolunteerDto } from '@/types/volunteer.types';
import { useFormValidation } from '@/hooks/useFormValidation';
import { volunteerEditValidationSchema } from '@/utils/validation-schemas-utils';
import { useContactExistsCheck, resolveContactCheckType } from '@/hooks/useContactExistsCheck';
import { ESTABLISHMENT_SEARCH_HINT } from '@/utils/format-establishment-select-label-utils';
import {
  commitSplitContactSwap,
  canSwapPrimaryContact,
  areContactsIdentical,
  contactExistsErrorMessage,
  isPrimaryContactFormValid,
  isPrimaryContactSubmitBlocked,
  primaryContactFieldError,
  resolveStoredContact,
  isPrimaryContactDirty,
  phoneToE164,
  readPhoneSetupLink,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { ContactChangeModal } from '@/components/common/modals/ContactChangeModal';
import { useContactChangeOnSubmit } from '@/hooks/useContactChangeOnSubmit';
import { Button } from '@/components/forms/Button';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { getPrimaryRole } from '@/utils/primary-role-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { useAuthStore } from '@/stores/authStore';
import { addDaysToDateString } from '@/utils/format-date-utils';
import {
  VOLUNTEER_EXIT_REASON_OPTIONS,
  getVolunteerFormDefaultRoleLabel,
  VOLUNTEER_FORM_QUERY_KEYS,
  GENDER_OPTIONS,
  STAFF_STATUS_FORM_OPTIONS,
} from '@/components/volunteers/volunteer-configs';
import {
  applyEditVolunteerField,
  applyVolunteerPrimaryContactChange,
  buildUpdateVolunteerDto,
  emptyEditVolunteerForm,
  formatTargetHoursInput,
  getEngagementEndOrderError,
  getEstablishmentHelperText,
  isEditVolunteerSubmitBlocked,
  formatVolunteerMutationSuccess,
  mapVolunteerSubjectOptions,
  parseTargetHoursInput,
  resolveEffectiveManagedById,
  shouldHideCoordinatorField,
  shouldTouchEngagementEndOnStartChange,
  volunteerEditValidationPayload,
  volunteerToEditFormData,
} from '@/components/volunteers/volunteer-form-utils';

/** Props for {@link EditVolunteerView}. */
interface EditVolunteerViewProps {
  volunteerId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Volunteer edit form. */
export const EditVolunteerView: React.FC<Readonly<EditVolunteerViewProps>> = ({
  volunteerId,
  onClose,
  onSuccess,
}) => {
  const authUser = useAuthStore((s) => s.user);
  const authRoles = useAuthStore((s) => s.roles);
  const { elements: volunteerElements = [] } = useInterfaceElements('volunteers');
  const availableRoles = React.useMemo(
    () => getEditableRoles(volunteerElements),
    [volunteerElements],
  );
  const isSingleRoleMode = availableRoles.length <= 1;
  const hideCoordinatorField = shouldHideCoordinatorField(authRoles);

  const defaultCountry = useDefaultPhoneCountry();
  const [phoneCountryCode, setPhoneCountryCode] = React.useState<string>(defaultCountry);
  const [formData, setFormData] = React.useState<EditVolunteerFormState>(emptyEditVolunteerForm);
  const [isHydrated, setIsHydrated] = React.useState(false);
  const [primaryContact, setPrimaryContact] = React.useState('');
  const [primaryContactType, setPrimaryContactType] = React.useState<'email' | 'phone'>('email');
  const [primaryContactCountryCode, setPrimaryContactCountryCode] = React.useState(defaultCountry);

  const validation = useFormValidation(volunteerEditValidationSchema);
  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;

  const {
    data: volunteer,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: VOLUNTEER_FORM_QUERY_KEYS.volunteer(volunteerId),
    queryFn: () => getVolunteer(volunteerId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'volunteerNotFound',
    onClose,
    entityId: volunteerId,
  });

  const secondaryContactType: 'email' | 'phone' =
    primaryContactType === 'email' ? 'phone' : 'email';
  const secondaryContactValue =
    primaryContactType === 'email' ? formData.phone || '' : formData.email || '';

  const { isChecking: isPrimaryChecking, exists: primaryContactExists } = useContactExistsCheck({
    value: primaryContact,
    type: resolveContactCheckType(primaryContactType),
    excludeUserId: volunteerId,
    skipHydratedValue: true,
  });

  const { isChecking: isSecondaryChecking, exists: contactExists } = useContactExistsCheck({
    value: secondaryContactValue,
    type: resolveContactCheckType(secondaryContactType),
    excludeUserId: volunteerId,
    skipHydratedValue: true,
  });

  const contactsAreIdentical = areContactsIdentical(primaryContact, secondaryContactValue);
  const primaryContactExistsError = primaryContactFieldError(
    primaryContactExists,
    primaryContactType,
    contactsAreIdentical,
  );
  const contactExistsError = contactExistsErrorMessage(contactExists, secondaryContactType);

  const { data: roles = [] } = useQuery({
    queryKey: VOLUNTEER_FORM_QUERY_KEYS.roles,
    queryFn: fetchRoles,
  });

  const { data: subjects = [] } = useQuery({
    queryKey: VOLUNTEER_FORM_QUERY_KEYS.subjects,
    queryFn: async () => {
      const response = await getSubjects();
      return response.data;
    },
  });

  const effectiveManagedById = resolveEffectiveManagedById(
    hideCoordinatorField,
    authUser?.id,
    formData.managedById,
  );

  const {
    isLoading: estabSearchLoading,
    onSearch: onEstabSearch,
    onLoadMore: onEstabLoadMore,
    hasMore: estabHasMore,
    getOptionsForValues: getEstabOptions,
    primeLabelCache: primeEstabLabelCache,
  } = useEstablishmentsMultiselect();

  useEnrichEstablishmentLabels(formData.establishmentIds, primeEstabLabelCache);

  const {
    options: coordinatorOptions,
    isLoading: coordinatorLoading,
    onSearch: onCoordinatorSearch,
  } = useAdminAutocomplete({
    roleCodes: ['coordinator'],
    enabled: !hideCoordinatorField,
  });

  /** Hydrate from GET volunteer and prime establishment labels so {@link MultiSelect} shows names. */
  React.useEffect(() => {
    if (!volunteer) return;
    const {
      formData: initialData,
      phoneCountryCode: nextPhoneCountry,
      labelEntries,
    } = volunteerToEditFormData(volunteer, {
      hideCoordinatorField,
      authUserId: authUser?.id,
      defaultCountry,
    });
    primeEstabLabelCache(labelEntries);
    setFormData(initialData);
    setIsHydrated(true);
    setPhoneCountryCode(nextPhoneCountry);
    const stored = resolveStoredContact(
      volunteer.email ?? null,
      volunteer.phone ?? null,
      volunteer.primaryContactKind,
    );
    setPrimaryContact(stored.displayValue);
    setPrimaryContactType(stored.mode);
    setPrimaryContactCountryCode(stored.isoCode);
    validation.validateInitialFormData(
      volunteerEditValidationPayload({
        form: initialData,
        primaryContact: stored.displayValue,
        primaryContactType: stored.mode,
        primaryContactCountryCode: stored.isoCode,
        phoneCountryCode: nextPhoneCountry,
        primaryContactExists: null,
        contactExists: null,
      }),
    );
  }, [volunteer, hideCoordinatorField, authUser?.id, primeEstabLabelCache, defaultCountry]);

  /** Close the panel after a non-self contact change (or a clean PATCH). */
  function handleContactChangeFinished(): void {
    onSuccess?.();
    onClose();
  }

  const isPrimaryDirty = isPrimaryContactDirty({
    mode: primaryContactType,
    currentValue: primaryContact,
    storedEmail: volunteer?.email,
    storedPhone: volunteer?.phone,
    serverKind: volunteer?.primaryContactKind,
    defaultCountry,
  });

  const contactChange = useContactChangeOnSubmit({
    isDirty: isPrimaryDirty,
    kind: primaryContactType,
    ownProfile: volunteerId === authUser?.id,
    closeForm: handleContactChangeFinished,
    fallbackPhone:
      primaryContactType === 'phone'
        ? phoneToE164(primaryContact, primaryContactCountryCode || defaultCountry)
        : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateVolunteerDto) => updateVolunteer(volunteerId, dto),
    onSuccess: (data) => {
      if (contactChange.isIntercepting) {
        contactChange.onMutationSettled({ ok: true, link: readPhoneSetupLink(data) });
        return;
      }
      const roleLabel =
        getPrimaryRole(volunteer?.userRoles)?.label || getVolunteerFormDefaultRoleLabel();
      toast.success(formatVolunteerMutationSuccess(roleLabel, 'edit'));
      handleContactChangeFinished();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
      contactChange.onMutationSettled({ ok: false });
    },
  });

  /** Set one field and re-validate (phone uses `_phoneCountryCode` context). */
  const handleFieldChange = (field: string, value: unknown) => {
    const updatedData = applyEditVolunteerField(formDataRef.current, field, value);
    setFormData(updatedData);

    const isSecondaryContact = field === 'phone' || field === 'email';

    if (field === 'phone') {
      validation.validateField(field, value, {
        ...updatedData,
        _phoneCountryCode: phoneCountryCode,
        _contactExists: isSecondaryContact ? contactExists : undefined,
      });
    } else {
      validation.validateField(field, value, {
        ...updatedData,
        _contactExists: isSecondaryContact ? contactExists : undefined,
      });
    }

    validation.markAsTouched(field);
    validation.markAsDirty(field);

    if (shouldTouchEngagementEndOnStartChange(field, value, updatedData.engagementEndDate)) {
      validation.markAsTouched('engagementEndDate');
    }
  };

  /** Mark a field touched so its error can show. */
  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Swap primary and secondary, then re-validate the contact field. */
  function handleSwapContacts(): void {
    setFormData(
      commitSplitContactSwap({
        primaryContact,
        primaryContactType,
        primaryCountryCode: primaryContactCountryCode,
        formData,
        secondaryCountryCode: phoneCountryCode,
        setPrimaryContact,
        setPrimaryContactType,
        setPrimaryContactCountryCode,
        setSecondaryCountryCode: setPhoneCountryCode,
        validation,
      }),
    );
  }

  /** Patch primary contact, keep form email/phone in sync, re-validate. */
  function handlePrimaryContactChange(
    value: string,
    type: 'email' | 'phone',
    countryCode?: string,
  ): void {
    setPrimaryContact(value);
    setPrimaryContactType(type);
    if (countryCode) setPrimaryContactCountryCode(countryCode);
    const updated = applyVolunteerPrimaryContactChange(formDataRef.current, value, type);
    setFormData(updated);
    validation.validateField(
      'contact',
      value,
      volunteerEditValidationPayload({
        form: updated,
        primaryContact: value,
        primaryContactType: type,
        primaryContactCountryCode: countryCode ?? primaryContactCountryCode,
        phoneCountryCode,
        primaryContactExists,
        contactExists,
      }),
    );
    validation.markAsTouched('contact');
    validation.markAsDirty('contact');
  }

  /** Validate then PATCH; blocked when engagement dates are inverted. */
  function handleSubmit(): void {
    if (
      isPrimaryContactSubmitBlocked(primaryContactExists, isPrimaryChecking, contactsAreIdentical)
    )
      return;
    const isValid = validation.validateForm(
      volunteerEditValidationPayload({
        form: formData,
        primaryContact,
        primaryContactType,
        primaryContactCountryCode,
        phoneCountryCode,
        primaryContactExists,
        contactExists,
      }),
    );
    if (isEditVolunteerSubmitBlocked(isValid, formData)) return;
    contactChange.requestSubmit(() => {
      updateMutation.mutate(
        buildUpdateVolunteerDto(formData, effectiveManagedById, {
          primaryContact,
          primaryContactType,
          primaryContactCountryCode,
          secondaryContact: secondaryContactValue,
          secondaryContactCountryCode: phoneCountryCode,
          defaultCountry,
        }),
      );
    });
  }

  const subjectOptions = mapVolunteerSubjectOptions(subjects);
  const volunteerRoleOptions = React.useMemo(
    () => buildCreatableRoleOptions(availableRoles, roles),
    [availableRoles, roles],
  );
  const engagementEndOrderError = getEngagementEndOrderError(
    formData.engagementStartDate,
    formData.engagementEndDate,
  );

  return (
    <>
      <SlidePanelForm
        formType="edit"
        title="Modifier le volontaire"
        subtitle="Modifiez les informations du volontaire"
        onClose={onClose}
        onSubmit={handleSubmit}
        submitLabel="Enregistrer"
        isSubmitting={updateMutation.isPending || contactChange.modal.open}
        isValid={
          isPrimaryContactFormValid({
            primaryExists: primaryContactExists,
            isPrimaryChecking,
            isSecondaryChecking,
            identical: contactsAreIdentical,
            formValid: validation.isFormValid(),
          }) && !engagementEndOrderError
        }
        isLoading={isLoading || (!!volunteer && !isHydrated)}
      >
        {!isSingleRoleMode && (
          <div className="form-section">
            <h4 className="form-section-title">
              <UserCog className="w-4 h-4" />
              Rôle(s)
            </h4>
            <MultiSelect
              label="Rôles"
              placeholder="Sélectionner les rôles"
              options={volunteerRoleOptions}
              value={formData.roleIds}
              onChange={(values) => handleFieldChange('roleIds', values)}
              onBlur={() => handleBlur('roleIds')}
              maxVisibleTags={3}
              helperText="Sélectionnez les rôles du volontaire"
              state={validation.getFieldState('roleIds')}
              error={validation.getFieldError('roleIds')}
            />
          </div>
        )}

        <div className="form-section">
          <h4 className="form-section-title">
            <User className="w-4 h-4" />
            Informations personnelles
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Prénom"
              value={formData.firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              onBlur={() => handleBlur('firstName')}
              placeholder="Ex: Marie"
              maxLength={100}
              required
              state={validation.getFieldState('firstName')}
              error={validation.getFieldError('firstName')}
            />
            <Input
              label="Nom"
              value={formData.lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              onBlur={() => handleBlur('lastName')}
              placeholder="Ex: Martin"
              maxLength={100}
              required
              state={validation.getFieldState('lastName')}
              error={validation.getFieldError('lastName')}
            />
            <Select
              label="Statut"
              value={formData.status}
              onChange={(value) => handleFieldChange('status', value)}
              onBlur={() => handleBlur('status')}
              options={[...STAFF_STATUS_FORM_OPTIONS]}
              state={validation.getFieldState('status')}
              error={validation.getFieldError('status')}
            />
            {formData.status === 'deactivated' && (
              <>
                <div className="col-span-full">
                  <DateSplitInput
                    label="Date de désactivation"
                    value={formData.exitDate}
                    onChange={(value) => handleFieldChange('exitDate', value)}
                    onBlur={() => handleBlur('exitDate')}
                    maxDate="2099-12-31"
                    required
                    state={validation.getFieldState('exitDate')}
                    error={validation.getFieldError('exitDate')}
                  />
                </div>
                <Select
                  label="Motif de sortie"
                  value={formData.exitReason ?? ''}
                  onChange={(value) => handleFieldChange('exitReason', value)}
                  onBlur={() => handleBlur('exitReason')}
                  options={[...VOLUNTEER_EXIT_REASON_OPTIONS]}
                  placeholder="Sélectionnez un motif (optionnel)"
                />
                {formData.exitReason === 'Autre' && (
                  <Input
                    type="text"
                    label="Précision du motif"
                    value={formData.exitReasonNote ?? ''}
                    onChange={(e) => handleFieldChange('exitReasonNote', e.target.value)}
                    onBlur={() => handleBlur('exitReasonNote')}
                    placeholder="Précisez le motif de sortie…"
                  />
                )}
              </>
            )}
            <Select
              label="Genre"
              value={formData.gender}
              onChange={(value) => handleFieldChange('gender', value)}
              onBlur={() => handleBlur('gender')}
              options={GENDER_OPTIONS}
              placeholder="Sélectionner"
              required
              state={validation.getFieldState('gender')}
              error={validation.getFieldError('gender')}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 mt-4">
            <SmartContactInput
              label="Contact principal (identifiant)"
              value={primaryContact}
              onContactChange={handlePrimaryContactChange}
              storedContact={{
                primaryKind: primaryContactType,
                serverPrimaryKind: volunteer?.primaryContactKind,
                email: volunteer?.email,
                phone: volunteer?.phone,
              }}
              error={primaryContactExistsError ?? validation.getFieldError('contact')}
              state={primaryContactExistsError ? 'error' : validation.getFieldState('contact')}
              showContactActions
              emailVerified={volunteer?.emailVerified}
              userId={volunteerId}
              swapButton={
                canSwapPrimaryContact(primaryContactType) ? (
                  <Button
                    type="button"
                    preset="contact-swap"
                    size="md"
                    onClick={handleSwapContacts}
                  />
                ) : undefined
              }
            />
            {primaryContactType === 'email' ? (
              <PhoneInput
                label="Numéro de téléphone (contact secondaire, optionnel)"
                value={formData.phone || ''}
                onChange={(e) => handleFieldChange('phone', e.target.value)}
                onBlur={() => handleBlur('phone')}
                onCountryChange={(country) => setPhoneCountryCode(country.code)}
                countryCode={phoneCountryCode}
                state={contactExistsError ? 'error' : validation.getFieldState('phone')}
                error={contactExistsError ?? validation.getFieldError('phone')}
                helperText="Numéro de contact secondaire"
              />
            ) : (
              <Input
                type="text"
                label="Email (contact secondaire, optionnel)"
                value={formData.email || ''}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
                icon={Mail}
                placeholder="Ex: contact@example.com"
                state={contactExistsError ? 'error' : validation.getFieldState('email')}
                error={contactExistsError ?? validation.getFieldError('email')}
                helperText="Email secondaire (le téléphone reste l'identifiant de connexion)"
              />
            )}
          </div>
        </div>

        {!hideCoordinatorField && (
          <div className="form-section">
            <h4 className="form-section-title">
              <Building2 className="w-4 h-4" />
              Coordinateur
            </h4>
            <Autocomplete
              label="Coordinateur superviseur"
              placeholder="Sélectionner un coordinateur"
              options={coordinatorOptions}
              value={formData.managedById}
              onChange={(value) => handleFieldChange('managedById', value)}
              onSearch={onCoordinatorSearch}
              isLoading={coordinatorLoading}
              minSearchLength={2}
              helperText="Le coordinateur qui supervise ce VSC"
              state={validation.getFieldState('managedById')}
              error={validation.getFieldError('managedById')}
            />
          </div>
        )}

        <div className="form-section">
          <h4 className="form-section-title">
            <Building2 className="w-4 h-4" />
            Établissements
          </h4>
          <MultiSelect
            label="Établissements"
            placeholder="Rechercher un établissement..."
            options={getEstabOptions(formData.establishmentIds)}
            value={formData.establishmentIds}
            onChange={(values) => handleFieldChange('establishmentIds', values)}
            onBlur={() => handleBlur('establishmentIds')}
            onSearch={onEstabSearch}
            onLoadMore={onEstabLoadMore}
            hasMore={estabHasMore}
            isSearchLoading={estabSearchLoading}
            searchPlaceholder="Rechercher un établissement..."
            minSearchLength={2}
            maxVisibleTags={2}
            showSelectAll={false}
            required
            helperText={getEstablishmentHelperText(
              !!effectiveManagedById,
              ESTABLISHMENT_SEARCH_HINT,
            )}
            state={validation.getFieldState('establishmentIds')}
            error={validation.getFieldError('establishmentIds')}
          />
        </div>

        <div className="form-section">
          <h4 className="form-section-title">
            <BookOpen className="w-4 h-4" />
            Matières enseignées
          </h4>
          <MultiSelect
            label="Matières"
            placeholder="Sélectionnez les matières"
            options={subjectOptions}
            value={formData.subjects}
            onChange={(values) => handleFieldChange('subjects', values)}
            onBlur={() => handleBlur('subjects')}
            maxVisibleTags={3}
            helperText="Sélectionnez les matières que le VSC peut enseigner"
            state={validation.getFieldState('subjects')}
            error={validation.getFieldError('subjects')}
          />
        </div>

        <div className="form-section">
          <h4 className="form-section-title">
            <Clock className="w-4 h-4" />
            Disponibilités
          </h4>
          <AvailabilityGrid
            value={formData.availability}
            onChange={(value) => handleFieldChange('availability', value)}
            disabled={false}
          />
          <p className="text-xs text-muted-foreground mt-2">
            Sélectionnez les créneaux de présence hebdomadaires du VSC
          </p>
        </div>

        <div className="form-section">
          <h4 className="form-section-title">
            <Calendar className="w-4 h-4" />
            Dates d'engagement
          </h4>
          <div className="grid grid-cols-1 gap-4">
            <DateSplitInput
              label="Date de début de mission"
              required
              value={formData.engagementStartDate}
              onChange={(value) => handleFieldChange('engagementStartDate', value)}
              onBlur={() => handleBlur('engagementStartDate')}
              state={validation.getFieldState('engagementStartDate')}
              error={validation.getFieldError('engagementStartDate')}
            />
            <DateSplitInput
              label="Date de fin de contrat"
              required
              value={formData.engagementEndDate}
              onChange={(value) => handleFieldChange('engagementEndDate', value)}
              onBlur={() => handleBlur('engagementEndDate')}
              minDate={addDaysToDateString(formData.engagementStartDate, 1)}
              maxDate="2099-12-31"
              yearSortAsc
              state={
                engagementEndOrderError ? 'error' : validation.getFieldState('engagementEndDate')
              }
              error={engagementEndOrderError ?? validation.getFieldError('engagementEndDate')}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Définissez la période d'engagement du VSC
          </p>
          <div className="grid grid-cols-1 gap-4 mt-4">
            <Input
              type="number"
              label="Nombre d'heures à réaliser"
              value={formatTargetHoursInput(formData.targetHours)}
              onChange={(e) =>
                handleFieldChange('targetHours', parseTargetHoursInput(e.target.value))
              }
              onBlur={() => handleBlur('targetHours')}
              placeholder="Ex: 50"
              min={0}
            />
          </div>
        </div>
      </SlidePanelForm>
      <ContactChangeModal
        open={contactChange.modal.open}
        kind={contactChange.modal.kind}
        ownProfile={contactChange.modal.ownProfile}
        phase={contactChange.modal.phase}
        phoneSetupLink={contactChange.modal.phoneSetupLink}
        onClose={contactChange.cancel}
        onConfirm={contactChange.confirm}
        onCopyLink={contactChange.onCopyLink}
      />
    </>
  );
};
